<template>
    <CieloLayout layout-version="v1">
        <LoadingScreen v-if="!this.hasOrderStatusData" :hide-header="true" />
        <div v-else class="order-status" :class="this.status">
            <OrderStatusHeader
                :status="this.status"
                :merchant-name="this.merchantName"
                :id-pay-redirect="this.idPayRedirect"
            />
            <CieloButtonV2
                v-if="this.status === 'idpay_pending' && this.idPayLink && !this.idPayRedirect"
                class="confirm-cta"
                @click="handleIdPayPendingCTA"
            >
                <span v-text="$ml.get('confirmIdentity')" />
            </CieloButtonV2>
            <OrderStatusOverview :status="this.status" />
            <CieloButtonV2
                v-if="this.status === 'denied' && this.shortUrl"
                class="retry-cta"
                @click="handleRetryCTA"
            >
                <span v-text="$ml.get('retry')" />
            </CieloButtonV2>
        </div>
    </CieloLayout>
</template>

<script>
import { logException, logEvent } from '@/helpers/cieloLogger';
import { mapGetters } from 'vuex';
import LoadingScreen from '@/components/LoadingScreen';
import OrderStatusHeader from '@/components/OrderStatus/OrderStatusHeader';
import OrderStatusOverview from '@/components/OrderStatus/OrderStatusOverview';

const CieloButtonV2 = () =>
  import(
    /* webpackChunkName: "cielo-button" */
    '@/components/template/CieloButtonV2'
  );
const CieloLayout = () =>
  import(
    /* webpackChunkName: "cielo-layout" */ '@/components/layout/CieloLayout'
  );

export default {
  components: {
    CieloLayout,
    LoadingScreen,
    OrderStatusHeader,
    OrderStatusOverview,
    CieloButtonV2,
  },

  data() {
    return {
      pollingInterval: null,
      incrementPollingFrequency: 10000, // 10 seconds in milliseconds
      currentPollingFrequency: 5000, // Start with the initial frequency
    };
  },


  async mounted() {
      await this.fetchOrderStatus(this.orderId);
      if(this.idPayRedirect && this.status === 'idpay_pending') {
          this.startPolling();
      }
      //when (and if) we add signalr to others payment methods just remove this.withPolling below and add new status scenarios
      if(this.withPolling && this.isOpenFinancePending) {
          await this.$store.dispatch('orderStatus/listenToRealTimeNotification');
      }
  },


  beforeDestroy() {
      if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
      }
  },

  computed: {
    ...mapGetters({
      hasOrderStatusData: 'orderStatus/hasOrderStatusData',
      status: 'orderStatus/getStatus',
      merchantName: 'orderStatus/getMerchantName',
      shortUrl: 'orderStatus/getShortUrl',
      idPayLink: 'orderStatus/getIdPayLink',
      hasPreorder: 'preorder/hasPreorder',
    }),
    orderId() {
        return this.$route.query.orderId;
    },
    idPayRedirect() {
        return this.$route.query.idPayRedirect;
    },
    withPolling() {
        return this.$route.query.polling;
    },
    isOpenFinancePending() {
        return this.status === 'openfinancepending';
    }
  },

  methods: {
    async fetchOrderStatus(orderId) {
      if (!orderId)
        return this.$store.dispatch('pipelineV2/pushTo', 'InvalidUrl');

      try {
        await this.$store.dispatch('orderStatus/fetchOrderStatus', orderId);
      } catch (error) {
        logException(error);

        if (this.hasPreorder) {
          return this.$store.dispatch('pipelineV2/pushTo', 'PaymentSuccess');
        } else {
          return this.$store.dispatch('pipelineV2/pushTo', 'InvalidOrder');
        }
      }
    },

    localizeOrderDate(orderDate, currentLocale) {
      const pattern = /(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})/;
      let date = new Date(orderDate.replace(pattern, '$3-$2-$1T$4:$5:$6Z'));

      return date.toLocaleString(currentLocale, {
        timeZone: 'UTC',
        dateStyle: 'medium',
        timeStyle: 'medium',
      });
    },

    handleRetryCTA() {
      window.location.href = this.shortUrl;
    },

    handleIdPayPendingCTA() {
      window.open(this.idPayLink, '_blank');

      logEvent({
        category: 'checkout',
        action: 'CTA_IdPayPendente',
        label: `clique no botão exibindo o OrderStatus: ${this.Status}`,
      });
    },

    startPolling() {
      this.pollingInterval = setInterval(this.checkOrderStatus, this.currentPollingFrequency);
    },

    async checkOrderStatus() {
      try {
        await this.$store.dispatch('orderStatus/pollingFetchOrderStatus', this.orderId);
        // Increase the polling interval by 10 seconds
        clearInterval(this.pollingInterval);
        this.currentPollingFrequency += this.incrementPollingFrequency;
        this.startPolling();
      } catch (error) {
        logException(error);
        // Retry with the same interval in case of an error
        this.startPolling();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.order-status {
  &.denied {
    @media screen and (max-width: 1024px) {
      display: flex;
      flex-direction: column;
      flex: 1 0;
    }

    .os-header {
      flex: 1 0;
    }

    .retry-cta {
      @media screen and (min-width: 1025px) {
        min-width: 0;
      }
    }
  }

  &.idpay_pending {
    .confirm-cta {
      margin-bottom: 20px;
      @media screen and (min-width: 1025px) {
        min-width: 0;
      }
    }
  }
}

.loading-container {
    position: relative;
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.circle {
	position: relative;
	font-size: 20px;
	font-weight: 700;
	line-height: 28px;
	color: #2F363E;
}


.loading-circle {
	position: absolute;
	width: 100px;
	height: 100px;
	border: 6px solid #DBE0E5;
	border-top: 6px solid #0774E7;
	border-radius: 50%;
	animation: spin 2s linear infinite;

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
}
</style>
